// ============================================================
// DisplayBeats - the attacks, as the display plays them (plan map-display,
// Track D; the beat grammar of the 2026-10-07 rulings).
//
// Over the figures: a shot's muzzle and its round drawn by damage type (an
// Energy beam, an Impact dash, an Explosive bolt that detonates late, a
// Rending needle); a melee strike's one bar sweeping a circle about the
// striker, its trail behind it; the landing's mark by damage type, a heavy
// hit's white frame, a critical's strobe (the only blink), the parry as the
// defender's own circle, a miss sparking on the floor behind, a kill's ring.
// An attacker this screen has no marker for (hidden, veiled) strikes from the
// screen's edge. Nothing here takes the pointer.
//
// BY ITS KIND (plan design-gaps, K3): a charge's rush streaks from the
// striker toward the target (the figure itself is shoved by the panel); a
// strike's wind-up draws its bar back, an all-out one wider and slower; swift
// and lightning sweep two and three bars; a guarded strike holds its guard
// ring after the landing; a feint swings half way and pulls back, landing
// nothing.
//
// The timing and the facts are `engine/display/beats`; this layer runs the
// beats' clock (useDisplayBeats) inside its own memoised subtree, while the
// panel reads the same beats only for the shoves and the kick
// (useBeatsOnMap); this only draws.
// ============================================================
import { DEFAULT_SIGNATURE_REACH, reachOf, type SignatureReach } from '../../engine/stealth/noise';
import type { Camera } from '../../engine/battlefield/projection';
import { SCENE_VIEW, project, projectGround } from '../../engine/battlefield/projection';
import { headingPoint } from '../../engine/battlefield/facing';
import type { Vec3 } from '../../engine/battlefield/types';
import type { ScreenXY } from '../battlefieldTokens/markerGeometry';
import { memo, useEffect, useMemo, type ReactNode } from 'react';
import { lerpXY } from '../../engine/display/timeline';
import { closedPath, pointsAttr } from '../../engine/display/svgPath';
import { barsOf, roundsOf, type Beat, type BeatPhase, type DrawnBeat } from '../../engine/display/beats';
import { noteBeatsShown } from '../../diagnostics/beatLog';
import BeatMark, { groundRing, onGround } from './BeatMarks';
import { sightWedge } from '../../engine/display/ambient';
import { useBeatFrame } from '../../hooks/useDisplayBeats';
import useHoldsStill from '../../hooks/useHoldsStill';
import BeatBurst from './BeatBurst';
import AreaBursts from './AreaBursts';
import clsx from '../../utils/clsx';
import styles from '../../styles/Display.module.css';

type XY = ScreenXY;
type Drawn = DrawnBeat;
/** What every part of a beat draws from: the beat, where it is, and its two ends on the screen. */
type PartProps = Pick<DrawnBeat, 'beat' | 'phase' | 'k'> & { from: XY; to: XY };
/** A shot's muzzle is kicked back this far along the line as the round leaves, screen units (the recoil). */
const RECOIL_PX = 6;

/** The strike's circle about the striker, screen units at the scene's own scale. */
const SWEEP_R = 26;
const ROUND_TAIL = { dash: 14, needle: 26 } as const;

/** Where an unseen attacker's beat comes from: the screen's nearer side edge, level with the target. */
const edgeOf = (to: XY): XY => ({ x: to.x < SCENE_VIEW.width / 2 ? 0 : SCENE_VIEW.width, y: to.y });

/** Memoised: the panel redraws on every hover and drag, and the beats change only with the impacts, markers or camera. */
export default memo(function DisplayBeats({
  beats,
  camera,
  inStealth = false,
  reach = DEFAULT_SIGNATURE_REACH,
}: {
  /** The beats this screen plays, as the panel worked them out once (useBeatsOnMap). */
  beats: readonly Beat[];
  camera: Camera;
  /** In Stealth an attack rings out from the attacker (plan map-display, H): its weapon's noise. */
  inStealth?: boolean;
  /** How far each Signature step carries at this table (plan armoury): the ring's reach. */
  reach?: SignatureReach;
}) {
  const still = useHoldsStill();
  const drawn: Drawn[] = useBeatFrame(beats, still);
  // While this layer is up, the screen owes every attack a beat (the fuzzer's oracle, engine/fuzz/beats).
  useEffect(() => {
    noteBeatsShown(Date.now());
    return () => noteBeatsShown(null);
  }, []);
  if (!drawn.length) return null;
  return (
    <g data-testid="display-beats" pointerEvents="none">
      {/* Suppressing fire's arc is hatched (K4): one pattern for the layer, only while an arc is drawn. */}
      {drawn.some((d) => d.beat.zone) && (
        <defs>
          <pattern id="beat-hatch" patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
            <line className={styles.beatHatch} x1={0} y1={0} x2={0} y2={6} />
          </pattern>
        </defs>
      )}
      {/* A blast's template, once however many it caught (T2): under the rounds that fly into it. */}
      <AreaBursts drawn={drawn} camera={camera} />
      {drawn.map((d) => (
        <BeatShape key={d.beat.id} {...d} camera={camera} inStealth={inStealth} reach={reach} />
      ))}
    </g>
  );
});

/** What the beat's group says it is, for a spec to read: facts that hold all through, and where it is. */
function beatData(beat: Beat, phase: BeatPhase | null) {
  const bars = beat.kind === 'strike' ? barsOf(beat.attack) : 1;
  return {
    'data-testid': 'display-beat',
    'data-kind': beat.kind,
    'data-look': beat.look ?? undefined,
    'data-mark': beat.mark ?? undefined,
    'data-phase': phase ?? undefined,
    'data-heavy': beat.heavy || undefined,
    'data-crit': beat.crit || undefined,
    'data-parried': beat.parried || undefined,
    'data-from': beat.from ? 'figure' : 'edge',
    'data-attack': beat.attack,
    'data-bars': bars > 1 ? bars : undefined,
    'data-rush': beat.rushM > 0 || undefined,
    'data-throw': beat.throwM > 0 || undefined,
    'data-rof': beat.rof ?? undefined,
    'data-bounce': beat.bounce ? true : undefined,
  };
}

/** One beat: its group says what it is (`beatData`); its parts are the phase's. */
function BeatShape({
  beat,
  phase,
  k,
  t,
  camera,
  inStealth,
  reach,
}: Drawn & { camera: Camera; inStealth: boolean; reach: SignatureReach }) {
  const to = project(beat.to, camera, SCENE_VIEW);
  const from = beat.from ? project(beat.from, camera, SCENE_VIEW) : edgeOf(to);
  return (
    <g {...beatData(beat, phase)} className={clsx(styles.beat, beat.crit && styles.beatCrit)}>
      {inStealth && <NoiseRing beat={beat} phase={phase} k={k} camera={camera} reach={reach} />}
      {beat.kind === 'shot' && <ShotParts beat={beat} phase={phase} k={k} from={from} to={to} camera={camera} />}
      {beat.kind === 'strike' && <StrikeParts beat={beat} phase={phase} k={k} from={from} to={to} />}
      {(phase === 'part' || phase === 'scatter') && <HordeParts beat={beat} phase={phase} k={k} camera={camera} />}
      {phase === 'quiet' && <QuietParts beat={beat} k={k} from={from} to={to} camera={camera} />}
      <Reactions beat={beat} phase={phase} k={k} from={from} to={to} />
      {beat.parried && (phase === 'strike' || phase === 'impact') && (
        <circle className={styles.beatParry} cx={to.x} cy={to.y} r={SWEEP_R * 0.8} />
      )}
      {phase === 'impact' && <Landing beat={beat} to={to} k={k} camera={camera} />}
      <BeatMark beat={beat} t={t} to={to} camera={camera} />
      {phase === 'emerge' && <circle className={styles.beatEmerge} cx={to.x} cy={to.y} r={30 - 22 * k} opacity={k} />}
      <BeatBurst beat={beat} t={t} from={from} to={to} />
      {phase === 'detonate' && (
        <circle className={styles.beatBurst} cx={to.x} cy={to.y} r={10 + 30 * k} opacity={1 - k} />
      )}
    </g>
  );
}

const RINGING: ReadonlySet<BeatPhase> = new Set(['strike', 'stop', 'impact']);
const RING_SIDES = 24;

/**
 * The attack's noise (plan map-display, H; the ruling: melee and silenced make a ring that does not spread): a
 * loud ring grows out across the ground as the attack goes off; a quiet one is a small ring that stays put. Drawn
 * only where this screen holds the attacker - an unseen attacker's ring would say where it stands.
 */
function NoiseRing({ beat, phase, k, camera, reach }: Omit<Drawn, 't'> & { camera: Camera; reach: SignatureReach }) {
  const at = beat.from;
  // Through the hit-stop too: the ring does not blink out between the strike and the landing.
  if (!beat.noise || !at || !phase || !RINGING.has(phase)) return null;
  const loud = beat.noise === 'loud';
  const grow = phase === 'strike' ? k : 1;
  // The step's reach at this table (plan armoury); a line from before the ladder rings as plainly loud.
  const full = reachOf(beat.signature ?? (loud ? 'loud' : 'silent'), reach);
  const r = (loud ? full * grow : full) || 0.01;
  // A circle on the GROUND, projected: under a tilted camera it is an ellipse, and its reach reads true.
  const points = pointsAttr(
    Array.from({ length: RING_SIDES }, (_, i) => {
      const a = (i / RING_SIDES) * Math.PI * 2;
      return projectGround({ x: at.x + r * Math.cos(a), z: at.z + r * Math.sin(a) }, camera);
    }),
  );
  return (
    <polygon
      className={styles.noiseRing}
      data-noise={loud ? 'loud' : 'silent'}
      data-reach={r.toFixed(1)}
      points={points}
      opacity={loud ? 1 - grow * 0.6 : 0.8}
    />
  );
}

/** The round in flight, drawn by its damage type. */
function Round({ beat, from, to, k }: { beat: Beat; from: XY; to: XY; k: number }) {
  const at = lerpXY(from, to, k);
  if (beat.look === 'bolt') return <circle className={styles.beatBolt} cx={at.x} cy={at.y} r={4} />;
  const len = Math.hypot(to.x - from.x, to.y - from.y) || 1;
  const tail = beat.look === 'beam' ? from : lerpXY(at, from, (ROUND_TAIL[beat.look ?? 'dash'] ?? 14) / len);
  return (
    <line
      className={clsx(styles.beatRound, beat.look && styles[`round_${beat.look}`])}
      x1={tail.x}
      y1={tail.y}
      x2={at.x}
      y2={at.y}
    />
  );
}

/** The phases a suppressing fire's arc stands on the ground through: from the muzzle to the landing. */
const ZONE_PHASES: ReadonlySet<BeatPhase> = new Set(['windup', 'strike', 'stop', 'impact']);

/**
 * A shot's parts by its rate of fire (K4): the muzzle's stars (one, two for semi-auto, three for a burst), the
 * rounds (one, two, a stream of four), suppressing fire's hatched arc on the ground, the cover's sparks.
 */
function ShotParts(props: PartProps & { camera: Camera }) {
  const { beat, phase, k, from, to, camera } = props;
  const rounds = roundsOf(beat.rof);
  return (
    <>
      {beat.zone && phase && ZONE_PHASES.has(phase) && <SuppressionZone zone={beat.zone} camera={camera} />}
      {phase === 'windup' && <MuzzleStars at={from} count={Math.min(3, rounds)} k={k} />}
      {phase === 'strike' && <Recoil from={from} to={to} k={k} />}
      {phase === 'strike' && <Rounds beat={beat} rounds={rounds} from={from} to={to} k={k} />}
      {phase === 'impact' && beat.cover && (
        <CoverSparks at={project(beat.cover.at, camera, SCENE_VIEW)} ap={beat.cover.ap} k={k} />
      )}
      {phase === 'impact' && beat.bounce && <Ricochet from={to} to={project(beat.bounce, camera, SCENE_VIEW)} k={k} />}
    </>
  );
}

/** How many bodies a broken horde's scatter draws fleeing outward, and how far they run past its ring. */
const SCATTER_RAYS = 9;
const SCATTER_RUN = 1.6;

/**
 * A horde's own beat (plan design-gaps, V4): split, a line parting its ring as the bodies draw apart; broken, its
 * bodies running out from the ring every way and thinning. On the ground, through the camera.
 */
function HordeParts({ beat, phase, k, camera }: { beat: Beat; phase: BeatPhase; k: number; camera: Camera }) {
  const { floor, ringM } = beat;
  const at = (deg: number, m: number) => onGround(floor, headingPoint(floor, deg, m), camera);
  if (phase === 'part') {
    const [a, b] = [at(beat.tilt + 90, ringM * k), at(beat.tilt - 90, ringM * k)];
    return (
      <line
        data-testid="beat-split"
        className={styles.beatHorde}
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
        opacity={1 - k * 0.5}
      />
    );
  }
  return (
    <g data-testid="beat-scatter" className={styles.beatHorde} opacity={1 - k}>
      {Array.from({ length: SCATTER_RAYS }, (_, i) => {
        const deg = (360 * i) / SCATTER_RAYS + beat.tilt;
        const p = at(deg, ringM * (0.6 + SCATTER_RUN * k));
        return <circle key={i} cx={p.x} cy={p.y} r={2.5} />;
      })}
    </g>
  );
}

/** The share of Observe's gesture its wedge takes to swing in (Z1). */
const SWEEP_SHARE = 0.45;
const SWEEP_FROM_DEG = -60;
const OBSERVE_M = 12;
/** Distract's noise runs out this far, metres; the hider's ring is this wide. */
const NOISE_OUT_M = 14;
const QUIET_RING_M = 0.5;

type QuietDraw = (p: { beat: Beat; k: number; from: XY; to: XY; camera: Camera; mover: Vec3 }) => ReactNode;

/** The last fifth of a gesture fades it out. */
const fadeOut = (k: number) => (k > 0.8 ? (1 - k) / 0.2 : 1);

/** Observe: the wedge swings in to the one watched over the first share of the gesture, then holds. */
const ObserveWedge: QuietDraw = ({ beat, k, camera, mover }) => {
  const turn = SWEEP_FROM_DEG * (1 - Math.min(1, k / SWEEP_SHARE));
  const ground = sightWedge({ x: mover.x, z: mover.z, elevation: mover.y }, beat.to, { turn, reach: OBSERVE_M });
  const points = pointsAttr(ground.map((p) => project(p, camera, SCENE_VIEW)));
  return <polygon data-testid="beat-quiet" className={styles.quietWedge} points={points} opacity={fadeOut(k)} />;
};

/** Distract: the noise runs out where it lands - the target, never where the mover stands. */
const DistractNoise: QuietDraw = ({ beat, k, camera }) => (
  <polygon
    data-testid="beat-quiet"
    className={styles.quietNoise}
    points={groundRing(beat.floor, NOISE_OUT_M * k + 0.2, camera)}
    opacity={(1 - k) * 0.8}
  />
);

/** Shadow: a dotted thread to the one followed. */
const ShadowThread: QuietDraw = ({ k, from, to }) => (
  <line
    data-testid="beat-quiet"
    className={styles.quietThread}
    x1={from.x}
    y1={from.y}
    x2={to.x}
    y2={to.y}
    opacity={fadeOut(k)}
  />
);

/** Move unseen, Hide: the mover's ring at their feet, fading to a ghost. */
const FadingRing: QuietDraw = ({ k, camera, mover }) => (
  <polygon
    data-testid="beat-quiet"
    className={styles.quietRing}
    points={groundRing(mover, QUIET_RING_M, camera)}
    opacity={1 - k}
  />
);

/** Each quiet move's gesture; those aimed at somebody draw nothing played on the mover alone. */
const QUIET_DRAWS: Record<string, { draw: QuietDraw; aimed: boolean }> = {
  observe: { draw: ObserveWedge, aimed: true },
  distract: { draw: DistractNoise, aimed: true },
  shadow: { draw: ShadowThread, aimed: true },
  moveUnseen: { draw: FadingRing, aimed: false },
  hide: { draw: FadingRing, aimed: false },
};

/**
 * A quiet move's gesture (plan design-gaps, Z1; the stealth and beats pages), never its result - drawn by its move
 * (`QUIET_DRAWS`), from the mover's place on the ground; none for an unseen mover.
 */
function QuietParts({ beat, k, from, to, camera }: { beat: Beat; k: number; from: XY; to: XY; camera: Camera }) {
  const how = QUIET_DRAWS[beat.attack];
  if (!how || !beat.from || (how.aimed && beat.attackerId === beat.targetId)) return null;
  const mover = { x: beat.from.x, y: beat.floor.y, z: beat.from.z };
  const Draw = how.draw;
  return <Draw beat={beat} k={k} from={from} to={to} camera={camera} mover={mover} />;
}

/**
 * A round the armour stopped (plan design-gaps, V2): a spark where it met the hull and the round glancing off, its
 * streak running out along the reflected line and fading.
 */
function Ricochet({ from, to, k }: { from: XY; to: XY; k: number }) {
  const head = lerpXY(from, to, Math.min(1, k * 1.6));
  const tail = lerpXY(from, to, Math.max(0, k * 1.6 - 0.35));
  return (
    <g data-testid="beat-ricochet" className={styles.beatRicochet} opacity={1 - k}>
      <Pulse testid="beat-ricochet-spark" className={undefined} at={from} r={4 + 6 * k} opacity={1} />
      <line x1={tail.x} y1={tail.y} x2={head.x} y2={head.y} />
    </g>
  );
}

/**
 * The recoil: the muzzle's flash kicked back along the line as the round leaves, settling. Drawn here, not as a
 * shove of the figure, so a plain shot never re-renders the whole panel (the Track K simplify).
 */
function Recoil({ from, to, k }: { from: XY; to: XY; k: number }) {
  const back = lerpXY(from, to, -RECOIL_PX / (Math.hypot(to.x - from.x, to.y - from.y) || 1));
  const at = lerpXY(back, from, k);
  return <circle data-testid="beat-recoil" className={styles.beatMuzzle} cx={at.x} cy={at.y} r={3 * (1 - k)} />;
}

/** The muzzle's stars: one per round the trigger lets go of, up to three, a little apart. */
function MuzzleStars({ at, count, k }: { at: XY; count: number; k: number }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <circle
          key={i}
          data-testid="beat-muzzle"
          className={styles.beatMuzzle}
          cx={at.x + i * 5}
          cy={at.y - i * 3}
          r={3 + 4 * k}
        />
      ))}
    </>
  );
}

/** The rounds in flight, one after another along the line: a stream for full auto. */
function Rounds({ beat, rounds, from, to, k }: { beat: Beat; rounds: number; from: XY; to: XY; k: number }) {
  return (
    <>
      {Array.from({ length: rounds }, (_, i) => {
        const ki = Math.min(1, k * (1 + 0.25 * (rounds - 1)) - i * 0.25);
        return ki > 0 ? <Round key={i} beat={beat} from={from} to={to} k={ki} /> : null;
      })}
    </>
  );
}

/** Suppressing fire's arc: a hatched wedge on the ground, the same on every screen (derived from the beat). */
function SuppressionZone({ zone, camera }: { zone: readonly Vec3[]; camera: Camera }) {
  // The wedge and the camera do not move over the beat: projected once, not every frame.
  const d = useMemo(() => closedPath(zone.map((p) => project(p, camera, SCENE_VIEW))), [zone, camera]);
  return <path data-testid="beat-zone" className={styles.beatZone} d={d} />;
}

/** The round meeting the cover: sparks off it and the AP it paid, "COVER · AP n". */
function CoverSparks({ at, ap, k }: { at: XY; ap: number; k: number }) {
  const r = 4 + 8 * k;
  return (
    <g data-testid="beat-cover" className={styles.beatCover} opacity={1 - 0.6 * k}>
      {[0, 1, 2, 3, 4].map((i) => {
        const a = -Math.PI / 2 + (i - 2) * 0.5;
        return <line key={i} x1={at.x} y1={at.y} x2={at.x + Math.cos(a) * r} y2={at.y + Math.sin(a) * r} />;
      })}
      <text x={at.x} y={at.y - r - 4} textAnchor="middle">{`COVER · AP ${ap}`}</text>
    </g>
  );
}

/**
 * What the blow met and how it began (K5): a dodge's ring parting about the target as the blow passes, and an
 * Overwatch shot's interrupt, the watcher's bracket snapping shut with "INTERRUPT" over the wind-up. A parry's
 * throw-back is the striker's own shove (the panel moves the figure), drawn by its circle.
 */
function Reactions({ beat, phase, k, from, to }: PartProps) {
  const passing = phase === 'strike' || phase === 'impact';
  return (
    <>
      {beat.dodged && passing && <DodgeRing at={to} k={phase === 'impact' ? 1 : k} />}
      {beat.attack === 'overwatch' && phase === 'windup' && <Interrupt at={from} k={k} />}
    </>
  );
}

/** The dodge: the target's ring split in two, the halves parting as the blow goes through. */
function DodgeRing({ at, k }: { at: XY; k: number }) {
  const r = SWEEP_R * 0.7;
  const gap = 4 + 10 * k;
  const half = (side: 1 | -1) =>
    `M ${at.x + side * gap} ${at.y - r} A ${r} ${r} 0 0 ${side === 1 ? 1 : 0} ${at.x + side * gap} ${at.y + r}`;
  return (
    <g data-testid="beat-dodge" className={styles.beatDodge} opacity={1 - 0.5 * k}>
      <path d={half(1)} />
      <path d={half(-1)} />
    </g>
  );
}

/** Overwatch's interrupt: the watcher's bracket closing on them, and the word. */
function Interrupt({ at, k }: { at: XY; k: number }) {
  const s = 26 - 12 * k;
  const corner = (dx: number, dy: number) =>
    `M ${at.x + dx * s} ${at.y + dy * (s - 7)} L ${at.x + dx * s} ${at.y + dy * s} L ${at.x + dx * (s - 7)} ${at.y + dy * s}`;
  return (
    <g data-testid="beat-overwatch" className={styles.beatInterrupt}>
      {[
        [-1, -1],
        [1, -1],
        [-1, 1],
        [1, 1],
      ].map(([dx, dy]) => (
        <path key={`${dx}${dy}`} d={corner(dx, dy)} />
      ))}
      <text x={at.x} y={at.y - s - 5} textAnchor="middle">
        INTERRUPT
      </text>
    </g>
  );
}

/** One ring about a point, its size and its fade the caller's: the ram's jolt, the wind-up, the guard. */
function Pulse(props: { testid: string; className: string | undefined; at: XY; r: number; opacity: number }) {
  const { testid, className, at, r, opacity } = props;
  return <circle data-testid={testid} className={className} cx={at.x} cy={at.y} r={r} opacity={opacity} />;
}

/**
 * A melee strike's parts by its kind (K3): the rush streak, the wind-up drawing the bar back (wider for an all-out
 * attack), the sweep (two bars for swift, three for lightning, each its share of the strike), the guard ring held
 * after a guarded strike, the feint's half swing pulled back.
 */
function StrikeParts({ beat, phase, k, from, to }: PartProps) {
  if (phase === 'rush') return <RushStreak from={from} to={to} k={k} />;
  if (phase === 'windup') return <Windup at={from} heavy={beat.attack === 'allOut' || beat.attack === 'ram'} k={k} />;
  // The body ram has no bar: the lunge was the blow, and it lands as a jolt at the target (K5).
  if (phase === 'stop' && beat.attack === 'ram')
    return <Pulse testid="beat-ram" className={styles.beatRam} at={to} r={10 + 8 * k} opacity={1 - 0.5 * k} />;
  if (phase === 'strike') return <Sweeps at={from} tilt={beat.tilt} bars={barsOf(beat.attack)} k={k} />;
  if (phase === 'guard')
    return <Pulse testid="beat-guard" className={styles.beatGuard} at={from} r={SWEEP_R * 0.9} opacity={1 - 0.6 * k} />;
  if (phase === 'feint') return <Sweep at={from} tilt={beat.tilt} k={0.5 * Math.sin(Math.PI * k)} feint />;
  return null;
}

/** The charge's rush: a streak from where the striker set off, toward the target, growing as they close. */
function RushStreak({ from, to, k }: { from: XY; to: XY; k: number }) {
  const head = lerpXY(from, to, 0.35 * k);
  return <line data-testid="beat-rush" className={styles.beatRush} x1={from.x} y1={from.y} x2={head.x} y2={head.y} />;
}

/** The wind-up: the bar's circle drawn in about the striker; an all-out one wider and heavier. */
function Windup({ at, heavy, k }: { at: XY; heavy: boolean; k: number }) {
  const r = SWEEP_R * (heavy ? 1.5 : 1.1) * (1 - 0.3 * k);
  const className = clsx(styles.beatWindup, heavy && styles.beatWindupHeavy);
  return <Pulse testid="beat-windup" className={className} at={at} r={r} opacity={0.3 + 0.7 * k} />;
}

/** Swift and lightning: the bars one after the other, each its share of the strike; one bar is the plain sweep. */
function Sweeps({ at, tilt, bars, k }: { at: XY; tilt: number; bars: number; k: number }) {
  return (
    <>
      {Array.from({ length: bars }, (_, i) => {
        const ki = Math.min(1, Math.max(0, k * bars - i));
        return ki > 0 ? <Sweep key={i} at={at} tilt={tilt + i * 40} k={ki} /> : null;
      })}
    </>
  );
}

/** The strike's one bar, sweeping a full circle about the striker in its own tilted plane, its trail behind it. */
function Sweep({ at, tilt, k, feint = false }: { at: XY; tilt: number; k: number; feint?: boolean }) {
  // The plane's tilt foreshortens the circle; capped by the engine so it never crosses head or ground.
  const ry = SWEEP_R * Math.max(0.25, Math.abs(Math.sin(((tilt + 45) * Math.PI) / 180)));
  const angle = k * Math.PI * 2;
  const end = { x: at.x + SWEEP_R * Math.cos(angle), y: at.y + ry * Math.sin(angle) };
  return (
    <>
      <ellipse
        className={styles.beatTrail}
        cx={at.x}
        cy={at.y}
        rx={SWEEP_R}
        ry={ry}
        pathLength={1}
        strokeDasharray={`${k} 1`}
      />
      <line className={clsx(styles.beatBar, feint && styles.beatFeint)} x1={at.x} y1={at.y} x2={end.x} y2={end.y} />
    </>
  );
}

/** The landing: the mark by damage type, the heavy hit's white frame, a miss's spark, a kill's ring. */
function Landing({ beat, to, k, camera }: { beat: Beat; to: XY; k: number; camera: Camera }) {
  const miss = beat.missAt ? project(beat.missAt, camera, SCENE_VIEW) : null;
  return (
    <>
      {miss && <circle className={styles.beatSpark} cx={miss.x} cy={miss.y} r={3 + 5 * k} opacity={1 - k} />}
      {beat.dead && <circle className={styles.beatDeath} cx={to.x} cy={to.y} r={14 + 24 * k} opacity={1 - k} />}
      {beat.heavy && k < 0.35 && (
        <rect
          className={styles.beatFlash}
          x={0}
          y={0}
          width={SCENE_VIEW.width}
          height={SCENE_VIEW.height}
          opacity={0.35 - k}
        />
      )}
    </>
  );
}
